import { JobsApiRequest, ServicesApiRequest } from '@/api'
import ServiceApiRequest from '@/api/services'
import { JobExpandablePropertyType, JobsFilterType, JobType, StatusCodeType } from '@/enum'
import {
  CustomFilterTypeExpression,
  DeleteTypeExpression,
  ItemTypeExpression,
  ListFilterTypeExpression,
  ListResponseTypeExpression
} from '@/enum/LinqExpressionType'
import JobRequestMapper from '@/mappers/JobRequestMapper'
import { CalendarEvent, JobUpdate, Service, ServiceForm } from '@/models'
import ErrorHandlerService from '@/services/ErrorHandlerService'
import { defineStore } from 'pinia'
import usePaginationStore from '../pagination'
import state from './state'
import type { IExplicitlyUpdatedJobProperty } from '@/interfaces'
import type { AxiosResponse } from 'axios'
import { fetchUntilExhaustion, generateFilterExpressionForCalendarEvents } from '@/utils/helpers'
import { expandedProperties, JOB_DETAILS_EXPAND_PARAMS, MESSAGE_CONSTANTS } from '@/utils/constants'
import ErrorType from '@/enum/ErrorType'
import useGlobalStore from '../global'
import NotificationType from '@/enum/NotificationType'

const useServicesStore = defineStore('services', {
  state: () => state,
  actions: {
    async getServices(storeName: string, responseOptions?: any) {
      const servicesApi = new ServicesApiRequest()
      const servicesList = usePaginationStore(storeName)
      servicesList.handleListAPICall(servicesApi.getServices.bind(servicesApi), Service, {
        ...responseOptions,
        [ListResponseTypeExpression.Expand]: JOB_DETAILS_EXPAND_PARAMS
      })
    },
    async getServicesForCalendar(start: string, end: string, options?: any) {
      const servicesApi = new ServicesApiRequest()
      const responseOptions = generateFilterExpressionForCalendarEvents(
        JobType.MAINTENANCE,
        start,
        end,
        options
      )
      const result = await fetchUntilExhaustion(
        servicesApi.getServices.bind(servicesApi),
        responseOptions
      )
      return result
        .map((job) => job)
        .filter((job) => job.scheduledAt || job.dueAt || job.startedAt || job.completedAt)
    },
    async createService(): Promise<AxiosResponse> {
      this.isServiceFormLoading = true
      const servicesApi = new ServicesApiRequest()
      const jobMapper = new JobRequestMapper()
      const globalStore = useGlobalStore()
      const newJob = jobMapper.toJobUpdate({ ...this.serviceForm, type: JobType.MAINTENANCE })

      const result = await servicesApi.createService(newJob?.jobRequestData, expandedProperties)

      try {
        if (result.status === StatusCodeType.Success && result.data) {
          // Prompt user
          globalStore.addToastNotification({
            id: Symbol('Job added'),
            message: `${result.data?.title} added successfully`,
            type: NotificationType.Success
          })
        }
      } catch (error: any) {
        ErrorHandlerService.handleError(error, error)
      } finally {
        this.isServiceFormLoading = false
      }
      return result
    },
    async getService(id: string) {
      this.isServiceFormLoading = true

      try {
        const servicesApi = new ServicesApiRequest(false, true)
        const result = await servicesApi.getService({
          id,
          ...expandedProperties
        })
        if (result.status === StatusCodeType.Success && result.data) {
          this.serviceForm = new ServiceForm(result.data)
        }
        return result
      } catch (error: any) {
        const title = error?.response.data?.detail

        const globalStore = useGlobalStore()
        globalStore.addError(
          {
            title
          },
          ErrorType.Network
        )
        return error
      } finally {
        this.isServiceFormLoading = false
      }
    },
    async updateService(
      id: string,
      explicitlyUpdatedServiceProperty?: IExplicitlyUpdatedJobProperty
    ) {
      const servicesApi = new ServiceApiRequest(false, true)
      const updatedService = new JobUpdate(explicitlyUpdatedServiceProperty ?? this.serviceForm)

      try {
        const result = await servicesApi.updateService(
          {
            id,
            [ItemTypeExpression.Expand]: JOB_DETAILS_EXPAND_PARAMS
          },
          updatedService
        )
        if (result.status === StatusCodeType.Success && result.data) {
          // Prompt user about successful about
        }
        return result
      } catch (error: any) {
        const title = error?.response.data?.detail ?? MESSAGE_CONSTANTS.SERVICE_WAS_DELETED_ALERT_TITLE
        if ([StatusCodeType.NotFound, StatusCodeType.Invalid].includes(error.status)) {
          const globalStore = useGlobalStore()
          globalStore.addError(
            {
              title
            },
            ErrorType.Network
          )
          return error
        }
        ErrorHandlerService.handleError(error, error)
      }
    },
    async deleteServices(storeName: string) {
      const servicesListStore = usePaginationStore(storeName)
      try {
        const servicesApi = new ServiceApiRequest()
        if (!servicesListStore.selectedItems) throw new Error('No services to delete')
        const result = await servicesApi.deleteServices({
          [DeleteTypeExpression.Expression]: { Id: servicesListStore.selectedItems }
        })
        return result
      } catch (error: any) {
        ErrorHandlerService.handleError(error, error)
      }
    },
    resetServiceForm() {
      this.serviceForm = new ServiceForm()
    },
    async getServicesUnshceduledJobs(storeName: string) {
      const unscheduleJobsListStore = usePaginationStore(storeName)
      const jobsApi = new JobsApiRequest()
      const expandableResources = [
        JobExpandablePropertyType.ASSIGNED_EMPLOYEES,   
        JobExpandablePropertyType.ASSIGNED_EMPLOYEES_EMPLOYEE,
        JobExpandablePropertyType.SCHEDULE
      ]

      const responseOptions = {
        [ListResponseTypeExpression.Expand]: expandableResources.join(','),
        [ListFilterTypeExpression.Filter]: {
          [CustomFilterTypeExpression.Expression]: `(${JobsFilterType.SCHEDULED_DATE}==null && ${JobsFilterType.START_DATE}==null)`,
          [JobsFilterType.TYPE]: JobType.MAINTENANCE
        }
      }

      unscheduleJobsListStore.loading = true

      try {
        await unscheduleJobsListStore.handleListAPICall<CalendarEvent>(
          jobsApi.getJobs.bind(jobsApi),
          CalendarEvent,
          responseOptions
        )
        return true
      } catch (error) {
        return false
      } finally {
        unscheduleJobsListStore.loading = false
      }
    }
  }
})

export default useServicesStore
